// Data Contracts
import { JudgeChambersSummary } from '@data-contracts';

// Local
import {
  JudgeChamberOption,
  JudgeChambersDocumentTag,
  JudgeChambersView,
} from '../models/judge-chambers-view.model';
import { mapChamberTags } from './map-chamber-tags.utils';

/**
 * Maps the judge chambers summary DTO to the page's UI model: the user's
 * chambers with their tags, each pending document's chamber, and the tags on
 * each pending document (names resolved from the chamber's tag list; tags whose
 * definition is missing are dropped). A document pending for more than one of
 * the user's chambers takes the first chamber.
 *
 * @param summary The judge chambers summary DTO.
 * @returns The judge chambers view.
 */
export function mapJudgeChambersSummary(
  summary: JudgeChambersSummary | null | undefined,
): JudgeChambersView {
  const chambers: JudgeChamberOption[] = [];
  const documentTags = new Map<number, JudgeChambersDocumentTag[]>();
  const documentChambers = new Map<number, string>();
  const caseChambers = new Map<number, string>();
  const documentNoteCounts = new Map<number, number>();
  const caseNoteCounts = new Map<number, number>();
  const documentAssignees = new Map<number, string>();
  const caseUniqueIds = new Map<number, string>();

  for (const chamber of summary?.chambers ?? []) {
    if (!chamber.chamberId) {
      continue;
    }
    for (const documentId of chamber.pendingDocumentIds ?? []) {
      if (!documentChambers.has(documentId)) {
        documentChambers.set(documentId, chamber.chamberId);
      }
    }

    const tags = mapChamberTags(chamber.tags);
    chambers.push({
      chamberId: chamber.chamberId,
      judgeName: chamber.judgeName?.fullName?.trim() ?? '',
      courtName: chamber.courtName ?? '',
      tags,
      members: (chamber.members ?? [])
        .filter(member => !!member.userUniqueId)
        .map(member => ({
          userUniqueId: member.userUniqueId ?? '',
          name:
            member.name?.fullName?.trim() ||
            [member.name?.lastName, member.name?.firstName]
              .filter(Boolean)
              .join(', '),
        })),
    });

    for (const assignment of chamber.documentAssignments ?? []) {
      if (
        assignment.documentId != null &&
        assignment.assigneeUserUniqueId &&
        documentChambers.get(assignment.documentId) === chamber.chamberId
      ) {
        documentAssignees.set(
          assignment.documentId,
          assignment.assigneeUserUniqueId,
        );
      }
    }

    for (const pendingCase of chamber.pendingCases ?? []) {
      if (pendingCase.caseId != null && pendingCase.caseUniqueId) {
        caseUniqueIds.set(pendingCase.caseId, pendingCase.caseUniqueId);
      }
    }
    for (const caseId of chamber.pendingCaseIds ?? []) {
      if (!caseChambers.has(caseId)) {
        caseChambers.set(caseId, chamber.chamberId);
      }
    }
    for (const count of chamber.documentNoteCounts ?? []) {
      if (
        count.id != null &&
        documentChambers.get(count.id) === chamber.chamberId
      ) {
        documentNoteCounts.set(count.id, count.count ?? 0);
      }
    }
    for (const count of chamber.caseNoteCounts ?? []) {
      if (
        count.id != null &&
        caseChambers.get(count.id) === chamber.chamberId
      ) {
        caseNoteCounts.set(count.id, count.count ?? 0);
      }
    }

    const tagNames = new Map(tags.map(tag => [tag.tagId, tag.name] as const));
    for (const documentTag of chamber.documentTags ?? []) {
      const name = tagNames.get(documentTag.tagId);
      if (documentTag.documentId == null || !name) {
        continue;
      }
      const applied = documentTags.get(documentTag.documentId) ?? [];
      applied.push({ tagId: documentTag.tagId, name });
      documentTags.set(documentTag.documentId, applied);
    }
  }

  for (const applied of documentTags.values()) {
    applied.sort((a, b) => a.name.localeCompare(b.name));
  }

  return {
    unreadNotificationCount: summary?.unreadNotificationCount ?? 0,
    chambers,
    documentTags,
    documentChambers,
    caseChambers,
    documentNoteCounts,
    caseNoteCounts,
    documentAssignees,
    caseUniqueIds,
  };
}
